import { useContext, useState } from "react";
import { format } from "date-fns";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Checkbox } from "primereact/checkbox";
import UiContext from "../../../../store/ui-context";
import useAPIRequest from "../../../../custom_hooks/simple/useAPIRequest";
import ChecklistProblemDialog from "../checklist/ChecklistProblemDialog";
import ChecklistRowDetail from "../checklist/ChecklistRowDetail";
import { isIgnoredProblem } from "../../utils/checklistProblem";

// Old open problems of a Foto Kendaraan card. Checking a row resolves the problem with a note + photo and
// tanggal_selesai = now; the row stays ticked until the page is reloaded.
// Pressing the description selects the problem (selectedId / onSelect), the card highlights its marker on the model.
const MarkingProblemTable = ({
  listProblemData,
  disabled,
  folderName,
  selectedId,
  onSelect,
  onResolved,
}) => {
  const uiContext = useContext(UiContext);
  const { requestPost, loading } = useAPIRequest();
  const [dialogTarget, setDialogTarget] = useState(null);
  // problem ids resolved in this session, so their row stays checked and visible instead of disappearing
  const [resolvedIds, setResolvedIds] = useState({});

  const handleToggleCek = (problem, checked) => {
    if (checked) setDialogTarget(problem);
  };

  const handleOnConfirmDialog = ({ note, gambar }) => {
    const problem = dialogTarget;
    setDialogTarget(null);

    requestPost({
      // /update merges onto the existing record (only the fields below change); /save overwrites the whole row,
      // which nulled out other fields (kendaraan_id among them) and made the problem drop out of the open-problem filter
      fullUrl: "/api/problem/problemkendaraan/update",
      body: {
        id: problem.id,
        status: 3,
        tanggal_selesai: format(new Date(), "yyyy-MM-dd HH:mm:ss"),
        note,
        gambar,
      },
      isForm: false,
      onError: ({ message }) => {
        uiContext.showErrorMsg("Error", message);
      },
      onSuccess: ({ message }) => {
        uiContext.showSuccessMsg("Success", message);
        setResolvedIds((prev) => ({ ...prev, [problem.id]: true }));
        if (onResolved) onResolved(problem.id);
      },
    });
  };

  const cekBodyTemplate = (problem) => (
    <div className="row-center-center">
      <Checkbox
        disabled={disabled || loading || !!resolvedIds[problem.id]}
        className="!p-0"
        checked={!!resolvedIds[problem.id]}
        onChange={(e) => handleToggleCek(problem, e.target.checked)}
      />
    </div>
  );

  const descriptionBodyTemplate = (problem) => {
    const dayCount = Math.floor(
      (new Date() - new Date(problem.tanggal)) / (1000 * 60 * 60 * 24)
    );
    const since = `Sejak ${format(new Date(problem.tanggal), "dd/MM/yyyy")}${
      dayCount > 0 ? ` (${dayCount} hari)` : ""
    }`;

    return (
      <div
        className={`col-start-start gap-1 w-full ${
          onSelect ? "cursor-pointer" : ""
        }`}
        onClick={() => onSelect && onSelect(problem)}
      >
        <div className="row-between-start w-full gap-2">
          <span>{problem.keterangan}</span>
          {isIgnoredProblem(problem) && (
            <span
              className="row-start-center gap-1 shrink-0 text-slate-400"
              title="Problem ini berstatus ignore"
            >
              <span className="text-xs font-semibold">Ignore</span>
              <i className="pi pi-eye-slash"></i>
            </span>
          )}
        </div>
        <span className="text-xs italic font-semibold text-slate-400">
          {since}
        </span>
        {(problem.note || problem.gambar) && (
          <ChecklistRowDetail
            note={problem.note}
            gambar={problem.gambar}
            alt={`problem_${problem.keterangan.replace(" ", "_")}`}
          />
        )}
      </div>
    );
  };

  return (
    <div className="col-start-start gap-2 w-full">
      <ChecklistProblemDialog
        visible={!!dialogTarget}
        mode="resolve"
        problem={dialogTarget}
        folderName={folderName}
        onConfirm={handleOnConfirmDialog}
        onHide={() => setDialogTarget(null)}
      />
      <DataTable
        loading={loading}
        className="w-full"
        value={listProblemData}
        rowClassName={(row) =>
          resolvedIds[row.id]
            ? "bg-green-50"
            : selectedId === row.id
            ? "bg-red-50"
            : ""
        }
      >
        <Column
          header={<i className="pi pi-check"></i>}
          headerClassName="text-xs"
          className="w-[2.5rem] !p-0"
          alignHeader={"center"}
          body={cekBodyTemplate}
        />
        <Column
          header="DESKRIPSI"
          headerClassName="text-xs"
          className="border-l-2"
          body={descriptionBodyTemplate}
        />
      </DataTable>
    </div>
  );
};

export default MarkingProblemTable;
